import React, { useContext, useState, useRef, useEffect } from "react";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { UserContext } from "../../../context/UserContext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Images from "../../../request/components/Images";
import { PRODUCT_TYPES_V2, QUOTE_TAGS } from "../../constants";
import { Autocomplete } from "@mui/material";
import QuillRTE from "../QuillRTE";
import currency from "currency.js";
import Price from "../Price";
import ItemVariantEntry from "./ItemVariantEntry";
import TextWithReadMore from "../../TextWithReadMore";
import {
  equalsIgnoreCase,
  getAmountWithTxnFee,
  getCatalogEntry,
  getItemBasePrice,
  getItemPrice,
  getItemSurchargeTotal,
  getProductTypeVariants,
  getVariantsBySize,
  isSalesTaxEnabled,
  isSalesTaxDefaultEnabled,
  pruneVariantFieldsOnSizeChange,
  applyPhotoVariantPricing,
  syncItemImagesWithPhotoVariant,
  syncPhotoVariantWithItemImages,
} from "../../util";
import RequestSummary from "../../../request/components/RequestSummary";
import DoneIcon from "@mui/icons-material/Done";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ButtonWithConfirmation from "../../ButtonWithConfirmation";
import bkstApi from "../../../api";
import QuantityPicker from "../QuantityPicker";
import TotalSurchargeLabel from "./TotalSurchargeLabel";
import AddCustomType from "../../../product/component/AddCustomType";
import AddExtraCharge from "../../../product/component/AddExtraCharge";
import ClearIcon from "@mui/icons-material/Clear";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { ExpandMoreOutlined } from "@mui/icons-material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

export default function CustomLineItemEntryPopup(props) {
  const { user } = useContext(UserContext);
  const ref = useRef();
  const errorRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { mode, immutablePrice, idx, isQuote } = props;

  const [tab, setTab] = useState("options");
  const [item, setItem] = useState(props.item || "");
  const [errors, setErrors] = useState([]);

  const [templates, setTemplates] = useState("");

  const [open, setOpen] = useState(false);

  const hasBasePrice = getItemBasePrice(item?.variant) > 0;

  const validate = () => {
    let arr = [];
    if (!item.type?.trim()) {
      arr.push("Item Type is required");
    }
    if (!(+item.price > 0)) {
      arr.push(hasBasePrice ? "Selected size must have a price" : "Item Price is required");
    }
    if (!(+item.quantity > 0)) {
      arr.push("Quantity is required");
    }

    // Check if size is required based on field configuration
    const fields = getCatalogEntry(user?.config, item.type)?.variants;
    if (fields?.size?.required && !item.variant?.size?.name) {
      arr.push("Size is required");
    }

    return arr;
  };

  const handleSubmit = () => {
    const arr = validate();
    setErrors(arr);

    if (arr.length > 0) {
      errorRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    } else {
      const cartItem = isSalesTaxEnabled(user.config)
        ? {
            ...item,
            salesTax: {
              enabled:
                item?.salesTax?.enabled !== undefined
                  ? `${item.salesTax.enabled}` === "true"
                  : isSalesTaxDefaultEnabled(user.config),
            },
          }
        : item;
      props.onChange(cartItem);
      handleClose();
    }
  };

  useEffect(() => {
    ref.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [item.type]);

  useEffect(() => {
    if (open && !templates) {
      bkstApi(`/template/quote`).then((res) => {
        setTemplates(res.data.sort((o1, o2) => (o1.name > o2.name ? 1 : -1)));
      });
    }
  }, [open]);

  const handleDelete = () => {
    props.onChange("", "delete");
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    setItem("");
    setTab("options");
    setErrors([]);
  };

  const updateTotal = ({ price, includeFees }, variant, quantity, images) => {
    const baseFromSize = getItemBasePrice(variant);
    if (baseFromSize > 0) {
      const priceWithSurcharge = getItemPrice(variant, baseFromSize);
      const total = currency(priceWithSurcharge).multiply(quantity).value;
      setItem({
        ...item,
        variant,
        price: baseFromSize,
        quantity,
        total,
        ...(images ? { images } : {}),
      });
      return;
    }

    const priceWithFees = includeFees ? getAmountWithTxnFee(price, user.config?.fees) : price;
    const surcharge = getItemSurchargeTotal(variant, price);
    const total = currency(priceWithFees).add(surcharge).multiply(quantity).value;

    setItem({
      ...item,
      variant,
      price,
      quantity,
      includeFees,
      total,
      ...(images ? { images } : {}),
    });
  };

  const handleItemTypeChange = (type) => {
    setItem({
      images: item.images,
      title: type,
      type,
      price: "",
      quantity: 1,
      includeFees: item.includeFees,
      salesTax: item.salesTax,
    });
  };

  if (!open && mode === "add") {
    return (
      <Button
        disableElevation
        startIcon={<AddIcon />}
        variant={idx === 0 ? "contained" : "outlined"}
        color={"primary"}
        onClick={() => {
          if (props.request) {
            // quote request
            setItem({
              images: props.request.pics || [],
              type: "Cake",
              title: "Cake",
              price: "",
              quantity: 1,
              includeFees: user.config?.transferFees === "false" ? false : true,
              salesTax: { enabled: isSalesTaxDefaultEnabled(user.config) },
              description:
                props.request?.details?.length > 2
                  ? `We can accommodate your request "${props.request.details}"`
                  : "",
            });
          } else {
            setItem({
              images: [],
              title: "",
              price: "",
              quantity: 1,
              includeFees: user.config?.transferFees === "false" ? false : true,
              salesTax: { enabled: isSalesTaxDefaultEnabled(user.config) },
            });
          }
          setOpen(true);
        }}
        fullWidth
        sx={{ minWidth: 0 }}
      >
        {props.cta || "CUSTOM ITEM"}
      </Button>
    );
  } else if (!open) {
    return (
      <IconButton
        color="primary"
        size="small"
        onClick={() => {
          setItem(props.item);
          setOpen(true);
        }}
      >
        <EditIcon />
      </IconButton>
    );
  } else {
    const fields = getVariantsBySize(getProductTypeVariants(user.config, item.type), item);

    return (
      <Dialog fullScreen={true} open={open} fullWidth={true}>
        <DialogTitle
          sx={{
            display: "flex",
            gap: "10px",
            alignItems: "flex-start",
            margin: "0",
            backgroundColor: "#DEDEDE",
            borderBottom: "1px solid #DDD",
            flexWrap: "wrap",
            padding: isMobile ? "8px 8px 16px" : "16px 24px",
          }}
        >
          <Box sx={{ flexGrow: "1", display: "flex", alignItems: "center" }}>
            <IconButton onClick={handleClose}>
              <ArrowBackIcon />
            </IconButton>
            {props.request ? (
              <Box>
                {props.request && <RequestSummary request={props.request} layout="dense" />}
              </Box>
            ) : (
              <Typography variant="h5">
                {mode === "add" ? "Add Custom Item" : "Edit Item"}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: hasBasePrice ? "center" : "flex-start",
              justifyContent: "right",
              flexGrow: "1",
              gap: "5px",
            }}
          >
            {hasBasePrice ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box sx={{ fontSize: "1.4rem", fontWeight: "700" }}>
                  {currency(item.price || 0).format()}
                </Box>
                <TotalSurchargeLabel item={item} />
              </Box>
            ) : (
              !immutablePrice && (
                <Box sx={{ maxWidth: "12rem" }}>
                  <Price
                    label={"Item Price"}
                    value={{ amount: item.price, includeFees: item.includeFees }}
                    onChange={(o) => {
                      updateTotal(
                        { price: o.amount, includeFees: o.includeFees },
                        item.variant,
                        item.quantity
                      );
                    }}
                  />
                  <TotalSurchargeLabel item={item} />
                </Box>
              )
            )}
            <QuantityPicker
              value={item.quantity}
              onChange={(quantity) => {
                updateTotal(
                  { price: item.price, includeFees: item.includeFees },
                  item.variant,
                  quantity
                );
              }}
            />
            <Button
              fullWidth
              color="primary"
              variant="contained"
              onClick={handleSubmit}
              sx={{
                height: "56px",
                whiteSpace: "nowrap",
                width: hasBasePrice ? "10rem" : undefined,
                maxWidth: "12rem",
              }}
              endIcon={<DoneIcon />}
              disabled={hasBasePrice && +(item?.total || "") <= 0}
            >
              {hasBasePrice
                ? currency(item.total || 0).format()
                : +item.total > 0
                  ? currency(item.total).format()
                  : "Save"}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#F7F7F7" }}>
          <Box ref={errorRef}>
            {errors.length > 0 && (
              <Alert sx={{ justifyContent: "center" }} severity="error">
                {errors.join(". ")}
              </Alert>
            )}
          </Box>
          <Box pt={4} ref={ref}>
            <Box>
              <Grid container>
                <Grid item sm={4} xs={6}>
                  <Box
                    pr={4}
                    pb={2}
                    sx={{ position: "-webkit-sticky", position: "sticky", top: "0" }}
                  >
                    <Images
                      edit={true}
                      pics={item.images}
                      onChange={(images) => {
                        const nextVariant = syncPhotoVariantWithItemImages(
                          images,
                          item.images,
                          item.variant,
                          fields
                        );
                        updateTotal(
                          { price: item.price, includeFees: item.includeFees },
                          nextVariant,
                          item.quantity,
                          syncItemImagesWithPhotoVariant(images, item.variant, nextVariant, fields)
                        );
                      }}
                      folder={`tmp`}
                    />
                  </Box>
                </Grid>
                <Grid item sm={8} xs={12}>
                  <Box mb={3}>
                    {!item.type && (
                      <Box mb={3}>
                        <Typography variant="h5" gutterBottom>
                          Item Type
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                          Select an item type below to see the relevant variants like size, flavor,
                          etc. Click <PlaylistAddIcon sx={{ verticalAlign: "middle" }} /> to enter a
                          custom item type.
                        </Typography>
                      </Box>
                    )}
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="type-label">Item Type</InputLabel>
                      <Select
                        value={item.type || ""}
                        labelId="type-label"
                        label="Item Type"
                        onChange={(e) => {
                          handleItemTypeChange(e.target.value);
                          document.activeElement?.blur();
                        }}
                        IconComponent={() => null}
                        endAdornment={
                          <InputAdornment sx={{ position: "absolute", right: 4 }} position="end">
                            {item.type ? (
                              <IconButton
                                onClick={() => {
                                  handleItemTypeChange("");
                                  document.activeElement?.blur();
                                }}
                              >
                                <ClearIcon></ClearIcon>
                              </IconButton>
                            ) : (
                              <AddCustomType
                                label={`Add Custom Item Type`}
                                onChange={(o) => {
                                  handleItemTypeChange(o.name);
                                  document.activeElement?.blur();
                                }}
                              >
                                <IconButton>
                                  <PlaylistAddIcon />
                                </IconButton>
                              </AddCustomType>
                            )}
                          </InputAdornment>
                        }
                      >
                        {(
                          (user.config?.catalog && Object.values(user.config?.catalog)) ||
                          PRODUCT_TYPES_V2
                        )
                          .sort((o1, o2) => o1.name.localeCompare(o2.name))
                          .map((o) => (
                            <MenuItem value={o.name}>{o.name}</MenuItem>
                          ))}
                        {item.type &&
                          !(Object.values(user.config?.catalog) || PRODUCT_TYPES_V2).find(
                            (o) => o.name === item.type
                          ) && <MenuItem value={item.type}>{item.type}</MenuItem>}
                      </Select>
                    </FormControl>
                  </Box>
                  {item.type && (
                    <Box>
                      <Box
                        sx={{
                          position: "-webkit-sticky",
                          position: "sticky",
                          top: "0",
                          backgroundColor: "#F7F7F7",
                          zIndex: "1000",
                          borderBottom: "10px solid #FFF",
                        }}
                      >
                        <Tabs
                          value={tab}
                          variant="scrollable"
                          scrollButtons="auto"
                          allowScrollButtonsMobile
                        >
                          <Tab
                            value="options"
                            label={"Options"}
                            onClick={() => setTab("options")}
                          />
                          <Tab value="notes" label={"Notes"} onClick={() => setTab("notes")} />
                        </Tabs>
                      </Box>
                      {tab === "options" && (
                        <Box>
                          <Paper elevation={0}>
                            <Box px={2} py={4}>
                              <ItemVariantEntry
                                basePrice={
                                  hasBasePrice
                                    ? getItemBasePrice(item.variant)
                                    : item.includeFees
                                      ? getAmountWithTxnFee(item.price, user.config?.fees)
                                      : item.price
                                }
                                productType={item.type}
                                fields={fields}
                                variant={item.variant || ""}
                                onChange={(variant) => {
                                  const pruned = pruneVariantFieldsOnSizeChange(
                                    variant,
                                    item.variant || {},
                                    fields
                                  );
                                  const synced = applyPhotoVariantPricing(pruned, fields);
                                  updateTotal(
                                    { price: item.price, includeFees: item.includeFees },
                                    synced,
                                    item.quantity,
                                    syncItemImagesWithPhotoVariant(
                                      item.images,
                                      item.variant,
                                      synced,
                                      fields
                                    )
                                  );
                                }}
                                isQuote={isQuote}
                              />
                              {hasBasePrice && (
                                <AddExtraCharge
                                  variant={item.variant || {}}
                                  onChange={(variant) =>
                                    updateTotal(
                                      { price: item.price, includeFees: item.includeFees },
                                      variant,
                                      item.quantity
                                    )
                                  }
                                />
                              )}
                            </Box>
                          </Paper>
                          {isSalesTaxEnabled(user.config) && (
                            <Box sx={{ marginTop: "15px", marginBottom: "15px" }}>
                              <Accordion elevation={0} defaultExpanded={false}>
                                <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                                  <Box style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                                    <SettingsOutlinedIcon
                                      style={{ fontSize: "1.3rem", color: "#777" }}
                                    />
                                    <Typography variant="subtitle1">Settings</Typography>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                    Override whether this custom item is taxable on the current cart.
                                  </Typography>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={
                                          item?.salesTax?.enabled !== undefined
                                            ? `${item.salesTax.enabled}` === "true"
                                            : isSalesTaxDefaultEnabled(user.config)
                                        }
                                        onChange={(e) =>
                                          setItem({
                                            ...item,
                                            salesTax: { enabled: e.target.checked },
                                          })
                                        }
                                      />
                                    }
                                    label="Apply sales tax to this item"
                                  />
                                </AccordionDetails>
                              </Accordion>
                            </Box>
                          )}
                          <Box my={2}>
                            <Button
                              size="large"
                              variant="contained"
                              color="secondary"
                              onClick={() => setTab("notes")}
                              fullWidth
                            >
                              CONTINUE
                            </Button>
                          </Box>
                        </Box>
                      )}
                      {tab === "notes" && (
                        <Box>
                          <Paper elevation={0}>
                            <Box px={2} py={4}>
                              <Box>
                                {(item.type === "Cake" ||
                                  equalsIgnoreCase(item.type, "mil hojas")) && (
                                  <Box mb={3}>
                                    <TextField
                                      id="inscription"
                                      value={item?.variant?.inscription?.name}
                                      label="Inscription"
                                      name="inscription"
                                      variant="outlined"
                                      fullWidth
                                      onChange={(e) =>
                                        setItem({
                                          ...item,
                                          variant: {
                                            ...item.variant,
                                            inscription: { name: e.target.value },
                                          },
                                        })
                                      }
                                    />
                                  </Box>
                                )}
                                <Box mb={3}>
                                  <Autocomplete
                                    value={item.tags || []}
                                    onChange={(e, tags) => setItem({ ...item, tags })}
                                    options={QUOTE_TAGS}
                                    filterSelectedOptions
                                    renderInput={(params) => (
                                      <TextField variant="outlined" {...params} label="Tags" />
                                    )}
                                    multiple
                                    freeSolo
                                  />
                                </Box>

                                <QuillRTE
                                  value={item.description || ""}
                                  onChange={(description) => setItem({ ...item, description })}
                                />
                                {templates && (
                                  <Box>
                                    {templates.map((t) => (
                                      <Chip
                                        key={t.name}
                                        size="small"
                                        label={t.name}
                                        component="a"
                                        onClick={() =>
                                          setItem({
                                            ...item,
                                            description: (item.description || "")?.concat(
                                              ` ${t.text}`
                                            ),
                                          })
                                        }
                                        clickable
                                      />
                                    ))}
                                  </Box>
                                )}
                                <Box py={0.4}>
                                  <TextWithReadMore
                                    data={`These notes are shown to customer. ${
                                      item.type === "Cake"
                                        ? `Use it to describe decor (edible image, ruffles, sugar flowers..), toppers (edible 3D
                      fondant, 2D cardstock) & more. Propose alternative options when sending quotes.`
                                        : ""
                                    }`}
                                    limit={140}
                                  />
                                </Box>
                              </Box>
                            </Box>
                          </Paper>
                          <Box my={2}>
                            <Button
                              size="large"
                              fullWidth
                              variant="outlined"
                              color="primary"
                              onClick={handleSubmit}
                            >
                              SAVE
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                  {mode !== "add" && !immutablePrice && (
                    <Box my={4}>
                      <ButtonWithConfirmation
                        copy="Are you sure you want to delete this item?"
                        onConfirm={handleDelete}
                      >
                        <Button color="error" fullWidth>
                          Delete Item
                        </Button>
                      </ButtonWithConfirmation>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }
}
